export type Step = {
  key: string
  header: string
  description: string
  goToLink: string
  goToText: string
  helpLink: string
  approximateMinutes: number
  minutesPer?: string
}

export type Section = {
  section: string
  title: string
  steps: Step[]
  isPrimaryGoal?: number
  canBePrimaryGoal?: boolean
}

const sectionsList: Section[] = [
  {
    section: 'kickoff-call',
    title: 'Kickoff Call',
    canBePrimaryGoal: false,
    steps: [
      {
        key: 'attend-kickoff-call',
        header: 'Attend Kickoff Call',
        description: 'This is your first call with the SynkedUP team which will kickoff your journey to success.  You will meet with your Customer Success Manager, and together, map out the path to achieve the outcomes you are here for.  This Success Plan is your path to profitability.',
        goToLink: '',
        goToText: '',
        helpLink: '',
        approximateMinutes: 45
      },
      {
        key: 'build-estimate',
        header: 'Build Initial Estimate And Template',
        description: 'During your kickoff call, your Customer Success Manager will walk you through building an estimate.  It will be super helpful if you have your current vendor pricing handy.  That way we can start to enter real data into your account!  If you have a REAL estimate to build, even better.  Have that information handy.',
        goToLink: '',
        goToText: '',
        helpLink: '',
        approximateMinutes: 0
      },
      {
        key: 'book-review-call',
        header: 'Book Budget Review Call',
        description: 'Your Customer Success Manager will schedule a call for you and your SynkedUP Financial Coach.  They will show you what homework you will need to prepare before that call.',
        goToLink: '',
        goToText: '',
        helpLink: '',
        approximateMinutes: 0
      },
      {
        key: 'book-strategy-call',
        header: 'Book Strategy Call',
        description: 'Your Customer Success Manager will schedule strategy calls with you in accordance with your Success Plan.',
        goToLink: '',
        goToText: '',
        helpLink: '',
        approximateMinutes: 0
      }
    ]
  },
  {
    section: 'system-setup',
    title: 'Get Your System Set Up',
    canBePrimaryGoal: false,
    steps: [
      {
        key: 'users',
        header: 'Enter all your users',
        description: 'Enter all of the users that will be logging into SynkedUP',
        goToLink: '/#!/users',
        goToText: 'Go to Users',
        helpLink: 'https://help.synkedup.com/en/articles/8440935-how-to-enter-your-synkedup-users',
        approximateMinutes: 3
      },
      {
        key: 'crew',
        header: 'Enter all of your crew members',
        description: 'Enter all of your crew that works out in the field so that their time can be tracked.',
        goToLink: '/#!/setup#crew-members',
        goToText: 'Go to Crew Members',
        helpLink: 'https://help.synkedup.com/en/articles/8291586-success-checklist-how-to-enter-crew-members',
        approximateMinutes: 3
      },
      {
        key: 'terms',
        header: 'Enter your terms and conditions',
        description: 'Enter your terms, conditions, contract language, etc so that each proposal has it automatically included.',
        goToLink: '/#!/proposal-settings#terms-and-conditions-card',
        goToText: 'Go to My Company',
        helpLink: 'https://help.synkedup.com/en/articles/8291617-success-checklist-how-to-enter-terms-and-conditions',
        approximateMinutes: 2
      },
      {
        key: 'divisions',
        header: 'Edit your divisions',
        description: 'Create the divisions you use in your company for scheduling and job costing purposes.',
        goToLink: '/#!/setup#divisions',
        goToText: 'Go to Divisions',
        helpLink: 'https://help.synkedup.com/en/articles/8291625-success-checklist-how-to-edit-divisions',
        approximateMinutes: 2
      },
      {
        key: 'qbo',
        header: 'Connect your QuickBooks',
        description: 'Connect your QuickBooks Online account with SynkedUP so you can import customers, process payroll, and begin invoicing.',
        goToLink: '/#!/setup#quickbooks-connect',
        goToText: 'Go connect QuickBooks',
        helpLink: 'https://help.synkedup.com/en/articles/8901545-synkedup-success-checklist-how-to-connect-to-your-quickbooks-online-account',
        approximateMinutes: 2
      },
      {
        key: 'cost-codes',
        header: 'Connect your cost codes',
        description: 'Connect your cost codes to the correct service codes in your QuickBooks account to track your revenue correctly.',
        goToLink: '/#!/setup#cost-codes',
        goToText: 'Go to Cost Codes',
        helpLink: 'https://help.synkedup.com/en/articles/8291667-success-checklist-how-to-set-up-and-connect-your-cost-codes',
        approximateMinutes: 10
      }
    ]
  },
  {
    section: 'budget',
    title: 'Budget Creation (Solve The Math Problem)',
    canBePrimaryGoal: false,
    steps: [
      {
        key: 'budget',
        header: 'Prepare Budget',
        description: 'Create your company budget to ensure your proposals are priced correctly, recovering your overhead, and earning profit. This is your homework to prepare for your budget call.',
        goToLink: '/#!/setup#budget-calculator',
        goToText: 'Go to Budget Worksheets',
        helpLink: 'https://help.synkedup.com/en/articles/8291676-success-checklist-how-to-create-your-budget',
        approximateMinutes: 60
      },
      {
        key: 'activate-budget',
        header: 'Activate Budget',
        description: 'This will happen on your budget call with your SynkedUP financial coach.  Once activated you will be pricing for profit using YOUR numbers!',
        goToLink: '',
        goToText: '',
        helpLink: '',
        approximateMinutes: 60
      }
    ]
  },
  {
    section: 'estimates',
    title: 'Create Your First Estimate (Solve The Resources Problem)',
    canBePrimaryGoal: true,
    steps: [
      {
        key: 'create',
        header: 'Create estimate',
        description: 'Enter all of the time and materials you are estimating for a job and enter descriptions for each work area. You’ll learn exactly what you need to charge to be profitable!',
        goToLink: '/#!/estimates',
        goToText: 'Go to Estimates',
        helpLink: 'https://help.synkedup.com/en/articles/8291717-success-checklist-how-to-create-your-first-estimate',
        approximateMinutes: 20
      },
      {
        key: 'proposal',
        header: 'Send proposal',
        description: 'Prepare your first proposal and send it off to your customer for approval 🤩.',
        goToLink: '',
        goToText: '',
        helpLink: 'https://help.synkedup.com/en/articles/8291735-success-checklist-how-to-send-a-proposal',
        approximateMinutes: 3
      }
    ]
  },
  {
    section: 'sold-jobs',
    title: 'Enter All Sold Jobs',
    canBePrimaryGoal: true,
    steps: [
      {
        key: 'create',
        header: 'Create all the sold jobs you have in your pipeline',
        description: 'Enter all of the jobs you have already sold so that you can schedule them and start tracking time and materials on them.',
        goToLink: '',
        goToText: '',
        helpLink: 'https://help.synkedup.com/en/articles/8291706-success-checklist-how-to-enter-and-already-sold-job',
        approximateMinutes: 10,
        minutesPer: 'per job'
      },
      {
        key: 'schedule',
        header: 'Schedule them on the calendar',
        description: 'Get your jobs scheduled on the calendar and assigned to a foreman.',
        goToLink: '/#!/calendar',
        goToText: 'Go to Calendar',
        helpLink: 'https://help.synkedup.com/en/articles/8291711-success-checklist-how-to-schedule-a-job',
        approximateMinutes: 1
      }
    ]
  },
  {
    section: 'train-crew',
    title: 'Train Your Crew',
    canBePrimaryGoal: true,
    steps: [
      {
        key: 'clock-in-and-out',
        header: 'Train your crew on clocking in and out daily on each job',
        description: 'Get your crew together and get them all familiar with how to clock in and out of the mobile app. Having the crew track time daily is essential for smooth payroll and job costing reports.',
        goToLink: '',
        goToText: '',
        helpLink: 'https://help.synkedup.com/en/collections/13751767-time-2-0',
        approximateMinutes: 30
      },
      {
        key: 'digital-job-folder',
        header: 'Train your crew on digital job folder to access job info and review progress',
        description: 'Teach your crew how to access all of the job information within the digital job folder, see their schedules, material lists for jobs, photos, and more.',
        goToLink: '',
        goToText: '',
        helpLink: 'https://help.synkedup.com/en/articles/11708749-how-to-view-job-details-on-the-mobile-app',
        approximateMinutes: 30
      }
    ]
  },
  {
    section: 'track-time',
    title: 'Start Tracking Time',
    canBePrimaryGoal: true,
    steps: [
      {
        key: 'track-time',
        header: 'Check this off once the crew is tracking time daily',
        description: 'Watch your tracked hours flow into your job costing reports, where you can see your estimated vs actual progress bars signaling your progress as you work on the job. The crew’s timesheets are the engine that drives your company’s data. They’re informing your next year’s budget, your production rates in templates, your estimating accuracy, your production capacity, your unbillable man-hour percentage, and more. The crew’s timesheets hold the secrets to your company’s success!',
        goToLink: '',
        goToText: '',
        helpLink: 'https://help.synkedup.com/en/articles/8291769-success-checklist-how-to-analyze-the-job-costing-on-a-job',
        approximateMinutes: 2,
        minutesPer: 'clock in/out sequence'
      }
    ]
  },
  {
    section: 'job-costing',
    title: 'Job Cost Your First Job',
    canBePrimaryGoal: true,
    steps: [
      {
        key: 'review',
        header: 'Review & analyze your first completed job’s estimated vs actual man-hours, material quantities, and expenses',
        description: 'Check out the job costing analysis on your job to see how well you estimated, how well the crew performed, and if you are making money!',
        goToLink: '',
        goToText: '',
        helpLink: 'https://help.synkedup.com/en/articles/8291769-success-checklist-how-to-analyze-the-job-costing-on-a-job',
        approximateMinutes: 10
      }
    ]
  },
  {
    section: 'invoicing',
    title: 'Invoicing',
    canBePrimaryGoal: true,
    steps: [
      {
        key: 'send',
        header: 'Send out the invoice for your first completed job',
        description: 'Put together an invoice for your customer, export it to QuickBooks Online, and get paid!',
        goToLink: '',
        goToText: '',
        helpLink: 'https://help.synkedup.com/en/articles/8291781-success-checklist-how-to-create-and-send-an-invoice',
        approximateMinutes: 5
      }
    ]
  }
]

export default sectionsList